/**
 * Camel illustration — a clean side-profile dromedary silhouette facing right.
 *
 * Art: "Camel" by Delapouite (https://game-icons.net), CC BY 3.0. Adapted from
 * the original single-path icon: the background square is dropped, the body is
 * filled with the camel's `light → body → dark` vertical gradient for form, and
 * a thin non-scaling accent outline keeps even the white camel readable on the
 * light papyrus background.
 *
 * `size` sets the glyph height in px; the glyph is square. Gradient ids are made
 * unique per instance via `useId` so stacked camels don't share defs. The crazy
 * camels (black & white) are mirrored to face the opposite way from the racers.
 */

import { useId } from 'react'
import type { CamelColor } from '@mdev/common'
import { CAMEL_HEX } from '../theme'

interface Props {
  color: CamelColor
  size?: number
  /** Drop the ground shadow (useful when tightly stacked). */
  noShadow?: boolean
}

/** The camel silhouette path (game-icons.net "Camel" by Delapouite, CC BY 3.0). */
const CAMEL_PATH =
  'M420.8 26.91c-11.4.76-23.7 4.65-33.6 10.29-5.3-4.86-13.5-10.52-19.3-6.11-12.5 9.46-2.4 20.76 6.8 27.94 5.5 35.86 20.7 93.17-9.8 105.97C317 183.1 308.4 36.14 241 37.94c-40.4 1.08-22.6 59.65-62.6 61.65-29.5 1.51-27.3-54.51-51.9-55.36-25.9-.9-44.62 18.9-57.71 86.97-25.63-.1-35.73 20.1-47.42 59.2-11.686 39-3 115.6 1.2 162.4l7.87-76.3c2.43 12 6.19 24.1 11.91 36.7 3.91 18.7 5.44 37.4 5.81 56-8.2 10.2-8.8 26.2-.42 35.5-.92 26.8-2.67 53.5-1.68 80.3 34.48.5 66.04-1 99.54 0 1.8-11.9-14.9-20.4-34.3-30.3.3-13.7.2-30.5 0-47.5 8.8-10.2 9-28.1-.2-36.8.1-21.3.8-38.6 3.3-43.9 8-17.1 20.6-31.9 29.1-47.2 28.7 5.3 59.7 2.9 91.9-4.7l.7 85.5c-7.7 11.3-8 27.7.3 37.8 4.7 29 .6 58.1.8 87.1h58c2.3-15-22.5-23.1-34.6-30.1 0-22.1-3.9-38.8-.4-60.3 5-9.9 5.3-21.5.4-30.8.9-33 3.3-66 10.7-99 1.6-.6 7.9-3.7 9.3-5.3l10.9 98.4c-5.6 11.9-4.4 27.3 4 36.7 6.6 30.1 4.5 59.5 7.9 89.6l61.2.8c.3-12.3-29.1-20-40.3-25.5-6.4-21.4-5.7-43.1-6.7-64.9 8-12.1 7.6-28.9-1.1-39.5.5-38.3 5.5-76.8 18.4-114.6 106.6-5.9 96.2-72 99.3-133.2 1.4-27.24 55.5 1.7 60-11.61 2.4-6.92 3.6-13.89 0-21.84-8.6-19.29-23.9-20.32-36.7-20.63-12.3-7.36-22.6-25.96-35.5-26.31zm6.7 19.58c4.9 2.64 3.8 7.47 2.7 10.11-6.6 1.96-16.3-1.08-20.8-4.59 3.9-2.99 12.2-5.39 18.1-5.52zM80.6 302.3c3.05 7.8 5.74 15.6 7.35 23.2 3.22 15.3 4.91 30.7 5.72 46.2-7.48 10.3-7.78 26.1.59 35-.25 21.6-1.3 43.2-1.52 64.7-4.54-7.5-12.92-14-24.94-17.1.16-14.4-.44-32.4-1.08-50.6 6.91-10.2 7.01-25.6-1.11-34.3-.67-27-.34-49.4 3.78-54.1 3.95-4.5 7.67-8.8 11.21-13z'

export function CamelGlyph(props: Props) {
  const { color, size = 28, noShadow = false } = props
  const c = CAMEL_HEX[color]
  const rawId = useId().replace(/:/g, '')
  const gradId = `cu-body-${rawId}`
  // The crazy camels (black & white) face the opposite way from the racers.
  const flipped = color === 'black' || color === 'white'

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        display: 'block',
        overflow: 'visible',
        transform: flipped ? 'scaleX(-1)' : undefined,
      }}
      aria-label={`${color} camel`}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c.light} />
          <stop offset="55%" stopColor={c.body} />
          <stop offset="100%" stopColor={c.dark} />
        </linearGradient>
      </defs>

      {!noShadow && (
        <ellipse cx="256" cy="498" rx="210" ry="16" fill="rgba(60, 36, 10, 0.18)" />
      )}

      <path
        d={CAMEL_PATH}
        fill={`url(#${gradId})`}
        stroke={c.accent}
        strokeWidth={1.3}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
